import { FC } from 'react';
import useNotVerifiedUsers from '@/api/useNotVerifiedUsers';
import useVerifyUsers from '@/api/useVerifyUsers';
import DataTable from '@/components/functional/DataTable/DataTable';
import { ColumnDef } from '@tanstack/react-table';
import Alert from '@/components/shared/Alert';

interface Organization {
  id: string;
  email: string;
  name: string;
  status: string;
}

const VerifyUsersPage: FC = () => {
  const { data: users, isLoading: isLoadingUsers } = useNotVerifiedUsers();
  const {
    mutateAsync: verifyUser,
    isError: isErrorVerification,
    isPending: isLoadingVerification,
    isSuccess: isSuccessVerification,
    error: errorVerification,
  } = useVerifyUsers();


  const columns: ColumnDef<Organization>[] = [
    {
      accessorKey: 'email',
      header: 'Email address',
    },
    {
      accessorKey: 'name',
      header: 'Organization Name',
    },
    {
      accessorKey: 'status',
      header: 'Status',
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <button
          onClick={() => handleVerifyUser(row.original)}
          disabled={isLoadingVerification}
          className="text-blue-600 hover:text-blue-900 font-medium"
        >
          Verify
        </button>
      ),
    },
  ];

  const handleVerifyUser = async (user: Organization) => {
    try {
      await verifyUser(user);
    } catch (error) {
      console.error('Failed to verify user:', error);
    }
  };

  if (isLoadingUsers) {
    return <div>Loading...</div>;
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Pending Verifications</h1>
      </div>

      {isSuccessVerification && (
        <Alert status="success">User verified successfully</Alert>
      )}

      {isErrorVerification && (
        <Alert status="error">
          {errorVerification instanceof Error ? errorVerification.message : 'Failed to verify user'}
        </Alert>
      )}

      <DataTable
        data={users || []}
        columns={columns}
        className="w-full"
        zebra
      />
    </div>
  );
};

export default VerifyUsersPage;
